/**
 * /equipment/pressure-control/drills/:drillId — one crew BOP drill
 * (api#1281, BOP-2). Append-only: no Edit, no Delete. Crew training is not
 * billable, so nothing here links to a ticket.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { usePcDrill } from '../../hooks/usePressureControl';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatInstant } from '../../lib/calendar-date';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PC_DRILLS, pcDay, pcDrillKindLabel, pcSeconds } from '../../lib/pressure-control';
import { DhEmpty, DhPanel, DhSpec } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';

const text = (v: string | null | undefined) => (v && v.trim() ? v : '—');

export default function PressureControlDrillDetailScreen() {
  const { drillId } = useParams<{ drillId: string }>();
  const back = useBackToList(PC_DRILLS);
  const tz = useTenantTimeZone();
  const query = usePcDrill(drillId);
  const failure = queryLoadFailure(query);
  const d = query.data ?? null;

  return (
    <div data-testid="pc-drill-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Crew BOP drill"
        title={d ? `${pcDrillKindLabel(d.drillKind)} drill · ${pcDay(d.drilledOn)}` : query.isLoading ? 'Loading…' : 'Crew drill'}
        onBack={back}
        backLabel="Back"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading drill" />
            </div>
          ) : (failure && isNotFound(queryFailureError(query))) || (!failure && !d) ? (
            <DhPanel eyebrow="Crew drill" title="Not found" testId="pc-drill-not-found">
              <DhEmpty>
                No such drill on this account.{' '}
                <Link to={PC_DRILLS} className="font-semibold underline underline-offset-2">Back to the drills</Link>
              </DhEmpty>
            </DhPanel>
          ) : failure ? (
            <DhPanel eyebrow="Crew drill" title="Drill" testId="pc-drill-load-failure">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this drill" />
            </DhPanel>
          ) : d ? (
            <>
              <DhPanel eyebrow="Crew training · not billed" title="The drill" testId="pc-drill-summary">
                <dl className="grid grid-cols-2 gap-4 px-4 py-4 md:grid-cols-4">
                  <DhSpec label="Drilled at" value={formatInstant(d.drilledAt, tz) ?? '—'} />
                  <DhSpec label="Kind" value={pcDrillKindLabel(d.drillKind)} />
                  <DhSpec label="Time to secure" mono value={pcSeconds(d.secondsToSecure)} />
                  <DhSpec label="Reference" value={text(d.contextRef)} />
                  <DhSpec label="Rig" value={text(d.rigName)} />
                  <DhSpec label="Stack" value={text(d.stackName)} />
                  <DhSpec label="Recorded" value={formatInstant(d.createdAt, tz) ?? '—'} />
                </dl>
                {d.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{d.notes}</p>}
              </DhPanel>
              <DhPanel
                eyebrow="Present"
                title="Crew"
                testId="pc-drill-crew"
                footer={`${d.crew.length} ${d.crew.length === 1 ? 'person' : 'people'}`}
              >
                {d.crew.length === 0 ? (
                  <DhEmpty>No crew is listed on this drill.</DhEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {d.crew.map((c) => (
                      <li key={c.userId} className="px-4 py-3 text-sm text-white">{c.name ?? '—'}</li>
                    ))}
                  </ul>
                )}
              </DhPanel>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}
